import { c as _c } from "react/compiler-runtime";
import { getEditorGroups, getMemberEditorProjects, getListStatus } from "@components/table/members/remove-xg-seat/remove-xg-seat-utils";
import { companyMemberGroupsSelector, companyMemberGroupsStatusSelector } from "@store/company-member-groups/company-member-groups-selector";
import { resetCompanyMemberGroupsState } from "@store/company-member-groups/company-member-groups-slice";
import { fetchCompanyMemberGroupDetails, fetchCompanyMemberGroups } from "@store/company-member-groups/company-member-groups-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { useEffect } from "react";

/**
 * Which groups to fetch the details of: only the ones in which the member is an editor, which is
 * enough to know whether they hold their group role directly, or all of them, to get their projects.
 */

/** Returns the groups of the scope whose details are fetched */
function getGroupsInScope(groups, scope) {
  return scope === "allGroups" ? groups : getEditorGroups(groups);
}

/** Returns whether the groups are shown, taking the details of the groups of the scope into account */
function getStatus(groupsStatus, detailsStatuses, scope) {
  if (groupsStatus !== "loaded") {
    return groupsStatus;
  }
  if (detailsStatuses.some(status => status === "pending" || status === "uninitialized")) {
    return "loading";
  }
  return scope === "allGroups" && detailsStatuses.includes("rejected") ? "failed" : "loaded";
}

/**
 * Fetches the groups a member belongs to and the details of the groups of the scope, freshly every
 * time it is used, since the previous steps may have changed them.
 *
 * @param memberIdentity The identity of the member
 * @param scope Which groups to fetch the details of
 */
export function useMemberGroupsWithDetails(memberIdentity, scope) {
  const $ = _c(16);
  const dispatch = useAppDispatch();
  const groups = useAppSelector(companyMemberGroupsSelector);
  const groupsStatus = useAppSelector(companyMemberGroupsStatusSelector);
  const groupDetailsStatus = useAppSelector(_temp);
  let t0;
  let t1;
  if ($[0] !== dispatch || $[1] !== memberIdentity || $[2] !== scope) {
    t0 = () => {
      dispatch(resetCompanyMemberGroupsState());
      let isActive = true;
      const detailRequests = [];
      const request = dispatch(fetchCompanyMemberGroups({
        memberId: memberIdentity
      }));
      request.unwrap().then(fetchedGroups => {
        if (!isActive) {
          return;
        }
        getGroupsInScope(fetchedGroups, scope).forEach(t2 => {
          const {
            id
          } = t2;
          detailRequests.push(dispatch(fetchCompanyMemberGroupDetails({
            groupId: id,
            userIdentity: memberIdentity
          })));
        });
      }).catch(_temp2);
      return () => {
        isActive = false;
        request.abort();
        detailRequests.forEach(_temp3);
        dispatch(resetCompanyMemberGroupsState());
      };
    };
    t1 = [dispatch, memberIdentity, scope];
    $[0] = dispatch;
    $[1] = memberIdentity;
    $[2] = scope;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[5] !== groupDetailsStatus || $[6] !== groups || $[7] !== groupsStatus || $[8] !== scope) {
    let t4;
    if ($[11] !== groupDetailsStatus) {
      t4 = t5 => {
        const {
          id: id_0
        } = t5;
        return groupDetailsStatus[id_0] ?? "uninitialized";
      };
      $[11] = groupDetailsStatus;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    const detailsStatuses = getGroupsInScope(groups, scope).map(t4);
    t2 = groups;
    t3 = getStatus(getListStatus(groupsStatus), detailsStatuses, scope);
    $[5] = groupDetailsStatus;
    $[6] = groups;
    $[7] = groupsStatus;
    $[8] = scope;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  let t4;
  if ($[13] !== t2 || $[14] !== t3) {
    t4 = {
      groups: t2,
      status: t3
    };
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}
function _temp3(detailRequest) {
  return detailRequest.abort();
}
function _temp2() {}
function _temp(state) {
  return state.companyMemberGroups.groupDetailsStatus;
}
/**
 * Fetches the projects in which a member is a manager, an admin or an editor, which come with the
 * details of the groups of the member, freshly every time it is used.
 *
 * @param memberIdentity The identity of the member
 */
export function useMemberEditorProjects(memberIdentity) {
  const $ = _c(6);
  const {
    groups,
    status
  } = useMemberGroupsWithDetails(memberIdentity, "allGroups");
  const projectsMap = useAppSelector(_temp4);
  let t0;
  if ($[0] !== groups || $[1] !== projectsMap) {
    t0 = getMemberEditorProjects(groups, projectsMap);
    $[0] = groups;
    $[1] = projectsMap;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== status || $[4] !== t0) {
    t1 = {
      projects: t0,
      status
    };
    $[3] = status;
    $[4] = t0;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
function _temp4(state) {
  return state.companyMemberGroups.projectsMap;
}